/**
 * CallOverlayModal Component
 *
 * Docked HUD card (fixed to the top-right) for in-progress call.
 * Shows status, matched cases chip, caller avatar/name, Decline and Accept buttons.
 * Collapse control returns to the top bar view.
 *
 * The overlay no longer blocks the page: it is a non-modal Paper surface so the
 * operator can keep working in the timeline while the call is up.
 */

import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import Popover from '@mui/material/Popover';
import Typography from '@mui/material/Typography';
import { alpha } from '@mui/material/styles';
import CallEndIcon from '@mui/icons-material/CallEnd';
import CallIcon from '@mui/icons-material/Call';
import CallMadeIcon from '@mui/icons-material/CallMade';
import MicOffIcon from '@mui/icons-material/MicOff';
import MicIcon from '@mui/icons-material/Mic';
import PhoneInTalkIcon from '@mui/icons-material/PhoneInTalk';
import PauseCircleOutlineIcon from '@mui/icons-material/PauseCircleOutline';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import RemoveIcon from '@mui/icons-material/Remove';

import type { QueueModel } from '../../domain/types';

// ============================================================================
// Types
// ============================================================================

export interface CallOverlayModalProps {
  open: boolean;
  queue: QueueModel | null;
  onAnswer: () => void;
  onEnd: () => void;
  onCollapse: () => void;
  showActions?: boolean;
  showInCallControls?: boolean;
  isMuted?: boolean;
  isOnHold?: boolean;
  onToggleMute?: () => void;
  onToggleHold?: () => void;
}

// ============================================================================
// Helpers
// ============================================================================

/** When is_incoming_call is true, treat as Incoming; otherwise Outbound. */
function getCallTypeLabel(queue: QueueModel): 'Incoming' | 'Outbound' {
  return queue.is_incoming_call ? 'Incoming' : 'Outbound';
}

function getCallStatusLabel(queue: QueueModel): string {
  switch (queue.status.toLowerCase()) {
    case 'ringing':
      return 'Ringing';
    case 'dialing':
      return 'Dialing';
    case 'active':
      return 'On Call';
    default:
      return 'Call';
  }
}

function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) {
    const first = parts[0]?.[0] ?? '';
    const last = parts[parts.length - 1]?.[0] ?? '';
    return `${first}${last}`.toUpperCase();
  }
  return name.slice(0, 2).toUpperCase();
}

// ============================================================================
// Component
// ============================================================================

export function CallOverlayModal({
  open,
  queue,
  onAnswer,
  onEnd,
  onCollapse,
  showActions = true,
  showInCallControls = false,
  isMuted = false,
  isOnHold = false,
  onToggleMute,
  onToggleHold,
}: CallOverlayModalProps) {
  const navigate = useNavigate();

  const [casesAnchor, setCasesAnchor] = useState<HTMLElement | null>(null);
  const casesOpen = Boolean(casesAnchor);

  const handleCasesClick = useCallback((e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    setCasesAnchor(e.currentTarget);
  }, []);

  const handleCasesClose = useCallback(() => {
    setCasesAnchor(null);
  }, []);

  const handleCaseSelect = useCallback((caseId: number) => {
    handleCasesClose();
    navigate(`/case-detail/${caseId}`);
  }, [handleCasesClose, navigate]);

  if (!open || !queue) return null;

  const typeLabel = getCallTypeLabel(queue);
  const statusLabel = getCallStatusLabel(queue);
  const isOutbound = !queue.is_incoming_call;
  const associatedCases = queue.associated_cases ?? [];
  const matchedCount = associatedCases.length;

  return (
    <Paper
      elevation={8}
      sx={{
        position: 'fixed',
        top: 16,
        right: 16,
        width: { xs: 'calc(100% - 32px)', sm: 360 },
        zIndex: t => t.zIndex.drawer + 1,
        borderRadius: 3,
        p: 3,
        pt: 2.5,
        overflow: 'visible',
        boxShadow: t => t.shadows[8],
      }}
    >
      {/* Header: status + chip + collapse */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 2,
          gap: 1,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {isOutbound ? <CallMadeIcon sx={{ fontSize: 20, color: 'primary.main' }} /> : <PhoneInTalkIcon sx={{ fontSize: 20, color: 'success.main' }} />}
          <Typography variant="body2" fontWeight={600} color={isOutbound ? 'primary.main' : 'success.main'}>
            {typeLabel} · {statusLabel}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {matchedCount > 0 && (
            <>
              <Chip
                size="small"
                label={`${String(matchedCount)} matched cases`}
                onClick={handleCasesClick}
                sx={{ bgcolor: 'action.hover', fontWeight: 500, cursor: 'pointer' }}
              />
              <Popover
                open={casesOpen}
                anchorEl={casesAnchor}
                onClose={handleCasesClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                slotProps={{ paper: { sx: { minWidth: 200, maxHeight: 300 } } }}
              >
                <Box sx={{ py: 0.5 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ px: 2, py: 1, display: 'block', fontWeight: 600 }}>
                    Matched Cases
                  </Typography>
                  {associatedCases.map(c => (
                    <ListItemButton key={c.id} onClick={() => handleCaseSelect(c.id)} dense>
                      <ListItemText
                        primary={c.loan_id}
                        secondary={c.status}
                        primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                        secondaryTypographyProps={{ variant: 'caption' }}
                      />
                    </ListItemButton>
                  ))}
                </Box>
              </Popover>
            </>
          )}
          <IconButton
            size="small"
            onClick={onCollapse}
            aria-label="Collapse to bar"
            sx={{
              bgcolor: 'action.hover',
              color: 'text.secondary',
              border: 1,
              borderColor: 'divider',
              '&:hover': { bgcolor: 'action.selected' },
            }}
          >
            <RemoveIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>

      {/* Caller: avatar + name */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          py: 2,
        }}
      >
        <Avatar
          sx={{
            width: 80,
            height: 80,
            bgcolor: t => `linear-gradient(180deg, ${alpha(t.palette.success.main, 0.9)} 0%, ${alpha(t.palette.success.dark, 0.95)} 100%)`,
            color: 'common.black',
            fontWeight: 700,
            fontSize: '1.5rem',
          }}
        >
          {getInitials(queue.name)}
        </Avatar>
        <Typography variant="h6" fontWeight={600} sx={{ mt: 1.5 }}>
          {queue.name}
        </Typography>
      </Box>

      {/* Actions: Decline + Accept */}
      <Box
        sx={{
          display: showActions ? 'flex' : 'none',
          gap: 2,
          justifyContent: 'center',
          mt: 2,
        }}
      >
        <Button
          variant="outlined"
          color="error"
          startIcon={<CallEndIcon />}
          onClick={onEnd}
          sx={{
            borderWidth: 1.5,
            textTransform: 'none',
            fontWeight: 600,
            px: 2.5,
            py: 1.25,
          }}
        >
          Decline
        </Button>
        <Button
          variant="outlined"
          color="success"
          startIcon={<CallIcon />}
          onClick={onAnswer}
          sx={{
            borderWidth: 1.5,
            textTransform: 'none',
            fontWeight: 600,
            px: 2.5,
            py: 1.25,
          }}
        >
          Accept
        </Button>
      </Box>

      {showInCallControls && (
        <Box
          sx={{
            display: 'flex',
            gap: 2,
            justifyContent: 'center',
            mt: 2,
          }}
        >
          {!showActions && (
            <Button
              variant="outlined"
              color="error"
              startIcon={<CallEndIcon />}
              onClick={onEnd}
              sx={{
                borderWidth: 1.5,
                textTransform: 'none',
                fontWeight: 600,
                px: 2.5,
                py: 1.25,
              }}
            >
              End
            </Button>
          )}
          <Button
            variant="outlined"
            color="info"
            startIcon={isMuted ? <MicOffIcon /> : <MicIcon />}
            onClick={onToggleMute}
            sx={{
              borderWidth: 1.5,
              textTransform: 'none',
              fontWeight: 600,
              px: 2.5,
              py: 1.25,
            }}
          >
            {isMuted ? 'Unmute' : 'Mute'}
          </Button>
          <Button
            variant="outlined"
            color="warning"
            startIcon={isOnHold ? <PlayCircleOutlineIcon /> : <PauseCircleOutlineIcon />}
            onClick={onToggleHold}
            sx={{
              borderWidth: 1.5,
              textTransform: 'none',
              fontWeight: 600,
              px: 2.5,
              py: 1.25,
            }}
          >
            {isOnHold ? 'Resume' : 'Hold'}
          </Button>
        </Box>
      )}
    </Paper>
  );
}
